// dates.js — Manejo de fechas único para todo el sistema.
//
// Por qué existe: cada página reimplementaba su propio parseo/formateo de
// fechas, con criterios distintos. De ahí salieron bugs repetidos:
//  - comparar fechas como STRING (Dashboard contaba mal los vencidos).
//  - format(parseISO(x)) con x inválido TIRA un RangeError en pleno render y
//    desmonta la app (crash al importar el Excel de deudores).
//  - formatos mezclados (ISO, dd/MM/yyyy, seriales de Excel) sin normalizar.
//
// Regla de oro: NINGUNA función de este módulo lanza una excepción. Ante una
// fecha inválida devuelven null / '' / '—' según corresponda.

import { format, parseISO } from 'date-fns';

// Convierte casi cualquier representación de fecha a un objeto Date real, o
// null si no se puede interpretar. Acepta: Date, ISO (yyyy-MM-dd...),
// dd/MM/yyyy, dd-MM-yyyy, yyyy/MM/dd y seriales de Excel (número de días).
export function parseFecha(val) {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val) ? null : val;

  const s = String(val).trim();
  if (!s) return null;

  // Serial de Excel (4-5 dígitos): días desde 1900-01-00
  if (/^\d{4,5}$/.test(s)) {
    const d = new Date((Number(s) - 25569) * 86400 * 1000);
    return isNaN(d) ? null : d;
  }

  // ISO yyyy-MM-dd (con o sin hora)
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(+m[1], +m[2] - 1, +m[3]);
    return isNaN(d) ? null : d;
  }

  // dd/MM/yyyy o dd-MM-yyyy
  m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
  if (m) {
    const d = new Date(+m[3], +m[2] - 1, +m[1]);
    return isNaN(d) ? null : d;
  }

  // yyyy/MM/dd
  m = s.match(/^(\d{4})[\/](\d{1,2})[\/](\d{1,2})/);
  if (m) {
    const d = new Date(+m[1], +m[2] - 1, +m[3]);
    return isNaN(d) ? null : d;
  }

  // Último intento: que lo resuelva el motor (ej. fechas con texto)
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

// Normaliza cualquier fecha a ISO 'yyyy-MM-dd', o '' si no es válida.
// Ideal para GUARDAR: así todo en la base queda en un único formato comparable.
export function aISO(val) {
  const d = parseFecha(val);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Formatea para MOSTRAR como dd/MM/yyyy. Nunca lanza: ante fecha inválida
// devuelve el placeholder (por defecto '—').
export function fmtFecha(val, placeholder = '—') {
  const d = parseFecha(val);
  if (!d) return placeholder;
  try {
    return format(d, 'dd/MM/yyyy');
  } catch {
    return placeholder;
  }
}

// Formatea con un patrón date-fns arbitrario, de forma segura.
export function fmtPatron(val, patron, placeholder = '—') {
  const d = parseFecha(val);
  if (!d) return placeholder;
  try {
    return format(d, patron);
  } catch {
    return placeholder;
  }
}

// Compara dos fechas a nivel de DÍA (ignora la hora). Devuelve -1, 0, 1, o
// null si alguna no es válida. Comparar por Date real, NUNCA por string.
export function compararFechas(a, b) {
  const da = parseFecha(a);
  const db = parseFecha(b);
  if (!da || !db) return null;
  const ta = new Date(da.getFullYear(), da.getMonth(), da.getDate()).getTime();
  const tb = new Date(db.getFullYear(), db.getMonth(), db.getDate()).getTime();
  return ta < tb ? -1 : ta > tb ? 1 : 0;
}

// ¿La fecha está vencida respecto a hoy (estrictamente anterior a hoy)?
export function estaVencida(val) {
  const hoy = new Date();
  return compararFechas(val, hoy) === -1;
}

// Días entre dos fechas (b - a) en días enteros, o null si alguna es inválida.
export function diasEntre(a, b) {
  const da = parseFecha(a);
  const db = parseFecha(b);
  if (!da || !db) return null;
  const ta = new Date(da.getFullYear(), da.getMonth(), da.getDate()).getTime();
  const tb = new Date(db.getFullYear(), db.getMonth(), db.getDate()).getTime();
  return Math.round((tb - ta) / 86400000);
}
